import React from 'react';
import { Target, Eye, Heart, Zap, Shield, Users, Star, TrendingUp } from 'lucide-react';
import FadeIn from '@/components/animations/FadeIn';

const MissionVisionSection: React.FC = () => {
  const impactStats = [
    { icon: Users, label: 'Pet Families', value: '10,000+', color: 'text-blue-600' },
    { icon: Shield, label: 'Records Secured', value: '50,000+', color: 'text-emerald-600' },
    { icon: Star, label: 'Emergency Saves', value: '500+', color: 'text-amber-600' },
    { icon: TrendingUp, label: 'Uptime', value: '99.9%', color: 'text-purple-600' }
  ];

  const coreValues = [
    {
      icon: Heart,
      title: 'Pet-First Design',
      description: 'Every feature prioritizes your pet\'s wellbeing and safety',
      gradient: 'from-rose-500 to-pink-600',
      bgGradient: 'from-rose-50/90 via-pink-50/80 to-red-50/70'
    },
    {
      icon: Shield,
      title: 'Uncompromising Security',
      description: 'Bank-level protection for your pet\'s sensitive health data',
      gradient: 'from-blue-500 to-cyan-600',
      bgGradient: 'from-blue-50/90 via-cyan-50/80 to-indigo-50/70'
    },
    {
      icon: Zap,
      title: 'Emergency Ready',
      description: 'Instant access to critical information when seconds count',
      gradient: 'from-amber-500 to-orange-600',
      bgGradient: 'from-amber-50/90 via-orange-50/80 to-yellow-50/70'
    }
  ];

  return (
    <section className="py-8 bg-gradient-to-br from-indigo-50 via-purple-50/80 to-blue-100/90 relative overflow-hidden">
      {/* Enhanced Background decorations with more texture */}
      <div className="absolute top-0 left-0 w-96 h-96 bg-gradient-to-br from-blue-300/20 to-purple-300/25 rounded-full blur-3xl -translate-x-48 -translate-y-48" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-gradient-to-br from-emerald-300/20 to-cyan-300/25 rounded-full blur-3xl translate-x-48 translate-y-48" />
      <div className="absolute top-1/3 left-1/2 w-80 h-80 bg-gradient-to-br from-pink-200/15 to-violet-200/20 rounded-full blur-2xl -translate-x-1/2" />
      
      {/* Subtle texture overlay */}
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(120,119,198,0.1),transparent_50%)]"></div>
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_80%,rgba(59,130,246,0.08),transparent_50%)]"></div>
      
      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        {/* Section Header */}
        <FadeIn>
          <div className="text-center mb-8">
            <div className="inline-flex items-center gap-4 px-8 py-4 bg-gradient-to-r from-blue-100/90 to-purple-100/90 backdrop-blur-sm rounded-full border border-blue-200/60 mb-4 text-xl font-bold shadow-xl">
              <Target className="w-8 h-8 text-blue-600" />
              <span className="bg-gradient-to-r from-blue-700 to-purple-700 bg-clip-text text-transparent">Our Mission & Vision</span>
            </div>
            <h2 className="text-4xl md:text-5xl font-bold mb-3">
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
                Transforming Pet Care Through Digital Innovation
              </span>
            </h2>
            <p className="text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed">
              From our leadership team's expertise to our revolutionary platform - discover the driving force behind 
              the world's most comprehensive digital pet identity solution.
            </p>
          </div>
        </FadeIn>

        {/* Mission & Vision Cards - New Design */}
        <div className="grid lg:grid-cols-2 gap-6 mb-12">
          {/* Mission Card */}
          <FadeIn delay={200}>
            <div className="group relative">
              {/* Card Background with Gradient Border */}
              <div className="absolute inset-0 bg-gradient-to-br from-blue-500 via-indigo-600 to-purple-700 rounded-3xl blur-sm opacity-75 group-hover:opacity-100 transition-opacity duration-300" />
              
              {/* Main Card Content */}
              <div className="relative bg-white/95 backdrop-blur-sm rounded-3xl p-6 border border-gray-100/50 shadow-2xl">
                {/* Header Section */}
                <div className="flex items-center gap-4 mb-5">
                  <div className="relative">
                    <div className="w-14 h-14 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <Target className="w-7 h-7 text-white" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                      <div className="w-1.5 h-1.5 bg-white rounded-full" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">Our Mission</h3>
                    <div className="h-1 w-20 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mt-2" />
                  </div>
                </div>

                {/* Mission Statement */}
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  To create the world's most trusted and comprehensive digital pet identity platform, 
                  ensuring every pet has secure, accessible health records that follow them throughout their entire life journey.
                </p>

                {/* Mission Points */}
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mt-2 flex-shrink-0" />
                    <span className="text-gray-600">Create a unique digital identity for every pet</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mt-2 flex-shrink-0" />
                    <span className="text-gray-600">Complete pet health documentation in one secure location</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mt-2 flex-shrink-0" />
                    <span className="text-gray-600">Emergency access to critical medical information</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full mt-2 flex-shrink-0" />
                    <span className="text-gray-600">Seamless veterinary communication and record sharing</span>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>

          {/* Vision Card */}
          <FadeIn delay={300}>
            <div className="group relative">
              {/* Card Background with Gradient Border */}
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-400 via-teal-600 to-cyan-700 rounded-3xl blur-sm opacity-75 group-hover:opacity-100 transition-opacity duration-300" />
              
              {/* Main Card Content */}
              <div className="relative bg-white/95 backdrop-blur-sm rounded-3xl p-6 border border-gray-100/50 shadow-2xl">
                {/* Header Section */}
                <div className="flex items-center gap-4 mb-5">
                  <div className="relative">
                    <div className="w-14 h-14 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-2xl flex items-center justify-center shadow-lg">
                      <Eye className="w-7 h-7 text-white" />
                    </div>
                    <div className="absolute -top-1 -right-1 w-5 h-5 bg-gradient-to-br from-pink-400 to-purple-500 rounded-full flex items-center justify-center">
                      <div className="w-1.5 h-1.5 bg-white rounded-full" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-2xl font-bold bg-gradient-to-r from-emerald-600 to-teal-600 bg-clip-text text-transparent">Our Vision</h3>
                    <div className="h-1 w-20 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full mt-2" />
                  </div>
                </div>

                {/* Vision Statement */}
                <p className="text-gray-700 text-lg leading-relaxed mb-6">
                  A world where every pet family has instant access to their pet's complete digital identity, 
                  enabling proactive healthcare, emergency preparedness, and seamless professional veterinary care.
                </p>

                {/* Vision Points */}
                <div className="space-y-3">
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full mt-2 flex-shrink-0" />
                    <span className="text-gray-600">Universal pet health record standards across all veterinary practices</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full mt-2 flex-shrink-0" />
                    <span className="text-gray-600">AI-powered health insights and preventive care recommendations</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <div className="w-2 h-2 bg-gradient-to-r from-emerald-500 to-teal-600 rounded-full mt-2 flex-shrink-0" />
                    <span className="text-gray-600">Global network of connected pet care professionals</span>
                  </div>
                </div>
              </div>
            </div>
          </FadeIn>
        </div>

        {/* Impact Statistics - Made more compact */}
        <FadeIn delay={400}>
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl p-4 shadow-lg border border-gray-100/50 mb-8">
            <div className="text-center mb-4">
              <h3 className="text-xl font-bold text-gray-900 mb-1">Real Impact, Real Results</h3>
              <p className="text-sm text-gray-600">The measurable difference we're making in pet families' lives every day</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {impactStats.map((stat, index) => (
                <div key={stat.label} className="text-center">
                  <div className="mb-2">
                    <div className={`w-10 h-10 bg-gradient-to-br ${stat.color === 'text-blue-600' ? 'from-blue-500 to-blue-600' : 
                      stat.color === 'text-emerald-600' ? 'from-emerald-500 to-emerald-600' :
                      stat.color === 'text-amber-600' ? 'from-amber-500 to-amber-600' :
                      'from-purple-500 to-purple-600'} rounded-xl flex items-center justify-center mx-auto shadow-lg`}>
                      <stat.icon className="w-5 h-5 text-white" />
                    </div>
                  </div>
                  <div className={`text-2xl font-bold ${stat.color} mb-1`}>{stat.value}</div>
                  <div className="text-xs font-medium text-gray-600">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </FadeIn>

        {/* Core Values in Action */}
        <FadeIn delay={500}>
          <div className="text-center mb-6">
            <h3 className="text-3xl font-bold text-gray-900 mb-3">Our Values in Action</h3>
            <p className="text-lg text-gray-700 max-w-2xl mx-auto">
              Every feature we build, every decision we make, is guided by these core principles that put pets and their families first.
            </p>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {coreValues.map((value, index) => (
              <FadeIn key={value.title} delay={600 + index * 100}>
                <div className={`bg-gradient-to-br ${value.bgGradient} backdrop-blur-sm rounded-2xl p-5 shadow-lg border border-white/60 hover:shadow-xl transition-all duration-300 hover:-translate-y-1 relative overflow-hidden`}>
                  <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent opacity-50"></div>
                  
                  <div className="relative z-10">
                    <div className={`w-14 h-14 bg-gradient-to-br ${value.gradient} rounded-2xl flex items-center justify-center mb-5 shadow-lg`}>
                      <value.icon className="w-7 h-7 text-white" />
                    </div>
                    <h4 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h4>
                    <p className="text-gray-600 leading-relaxed">{value.description}</p>
                  </div>
                </div>
              </FadeIn>
            ))}
          </div>
        </FadeIn>
      </div>
    </section>
  );
};

export default MissionVisionSection;
